import { createApi } from '@reduxjs/toolkit/query/react';

import { FORM_ITEMS } from '@/constants/filter';
import { normalizeProduct, normalizeProductsList } from '@/normalize';
import {
  getProductsIdsApiRequestUrl,
  getProductsListApiRequestUrl,
  request,
} from '@/utils';

const getFilters = (params) =>
  FORM_ITEMS.reduce((res, cur) => {
    const currentKey = cur.id;
    const defaultValue = cur?.defaultValue;
    const currentData = params?.[currentKey] || defaultValue;

    if (!!currentData?.length && cur?.submitType === 'miltySelect') {
      const strData = currentData
        ?.split(',')
        ?.reduce(
          (result, current) => `${result}&${cur.submitKey}[]=${current}`,
          ''
        );

      return `${res}${strData}`;
    }

    return currentData ? `${res}&${cur.submitKey}=${currentData}` : res;
  }, '');

const getAdditionalFilters = (params) => {
  const isRwkPage = !!params.companySlug;
  const isProductPage = !!params.productId;

  if (isRwkPage) {
    return '&is_in_rvk_minfin=1';
  }

  if (isProductPage) {
    return '';
  }

  return `&is_in_minfin_catalog=1&showcase=https://minfin.com.ua${params?.pathname}`;
};

const fetchProductsSkusIds = async (props) => {
  const filters = getFilters(props.params || {});
  const additionalFilters = getAdditionalFilters(props.params || {});

  const ids = await request(
    getProductsIdsApiRequestUrl({
      filters,
      additionalFilters,
    })
  );

  return ids?.data?.flat()?.join(',') || '';
};

export const fetchSkusList = async (props) => {
  try {
    const ids = await fetchProductsSkusIds(props);

    const result = await request(
      getProductsListApiRequestUrl({
        locale: props.locale,
        ids,
      })
    );

    return {
      data: normalizeProductsList({ data: result.data }),
    };
  } catch (error) {
    console.error('fetchSkusList error:', error);

    return { data: [] };
  }
};

const fetchSkusIdsByProduct = async (props) => {
  const filters = [
    `&product_id=${props?.params.productId}&currency=${props?.params.currency}`,
  ];

  const ids = await request(getProductsIdsApiRequestUrl({ filters }));

  return ids?.data?.flat()?.join(',') || [];
};

export const fetchSkusListByProduct = async (props) => {
  try {
    const ids = await fetchSkusIdsByProduct(props);

    const result = await request(
      getProductsListApiRequestUrl({
        locale: props.locale,
        ids,
      })
    );

    const skuSlug = props?.params.skuSlug;

    const main = result?.data?.find(({ slug }) => slug === skuSlug);
    const other = result?.data?.filter(({ slug }) => slug !== skuSlug);

    const productData = main ? { ...main, other } : null;

    return {
      data: productData ? normalizeProduct({ data: productData }) : {},
    };
  } catch (error) {
    console.error('fetchSkusListByProduct error:', error);

    return { data: {} };
  }
};

export const productsApi = createApi({
  reducerPath: 'products',
  endpoints: (builder) => ({
    getProductsList: builder.query({
      queryFn: fetchSkusList,
    }),
    getProductSkus: builder.query({
      queryFn: fetchSkusListByProduct,
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetProductsListQuery,
  useGetProductSkusQuery,
  util: { getRunningQueriesThunk: getRunningOperationPromisesProducts },
} = productsApi;

// export endpoints for use in SSR
export const { getProductsList, getProductSkus } = productsApi.endpoints;
